import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, Share2, Award } from "lucide-react";
import { certificates, courses } from "../data/mockData";
import { LOGO_URL } from "../constants";

export default function CertificateViewer() {
    const { certId } = useParams();
    const cert = certificates.find((c) => c.id === certId) || certificates[0];
    const course = courses.find((c) => c.id === cert.courseId);

    return (
        <div className="min-h-screen bg-ink-900 text-white">
            <div className="no-print sticky top-0 z-30 bg-ink-900/80 backdrop-blur border-b border-white/5">
                <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                    <Link to="/certificates" data-testid="back-cert-list" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
                        <ArrowLeft className="w-4 h-4" /> All certificates
                    </Link>
                    <div className="flex gap-3">
                        <button data-testid="share-cert" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5 transition-colors">
                            <Share2 className="w-4 h-4" /> Share
                        </button>
                        <button onClick={() => window.print()} data-testid="print-cert" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">
                            <Printer className="w-4 h-4" /> Print
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
                <div className="print-cert relative aspect-[1.414/1] rounded-3xl overflow-hidden p-12 lg:p-20 text-center" style={{ background: "linear-gradient(135deg, #fdfaf3 0%, #f5eeda 100%)", color: "#1a1a1a" }}>
                    {/* Ornate borders */}
                    <div className="absolute inset-6 border-2 border-double border-[#1a1a1a]/40 rounded-2xl pointer-events-none" />
                    <div className="absolute inset-10 border border-[#1a1a1a]/15 rounded-2xl pointer-events-none" />

                    {/* Brand */}
                    <div className="relative flex items-center justify-center mb-6">
                        <div className="bg-black rounded-xl px-4 py-2">
                            <img src={LOGO_URL} alt="Blue Mountain Group" className="h-12 w-auto object-contain" />
                        </div>
                    </div>

                    <div className="relative">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#1a1a1a]/60 font-semibold">Certificate of Completion</div>
                        <div className="font-display font-black text-3xl lg:text-5xl mt-3 tracking-tight">This certifies that</div>
                        <div className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter mt-6 italic" style={{ color: "#7C5CFF" }}>
                            {cert.studentName}
                        </div>
                        <div className="mt-6 max-w-2xl mx-auto leading-relaxed text-base lg:text-lg">
                            has successfully completed the course
                        </div>
                        <div className="font-display font-bold text-2xl lg:text-3xl mt-3 tracking-tight">
                            {cert.courseTitle}
                        </div>
                        <div className="mt-2 text-sm text-[#1a1a1a]/60">
                            {course && `${course.durationHours} hours · `}{course?.category} · Grade <span className="font-bold text-[#7C5CFF]">{cert.grade}</span>
                        </div>

                        {/* Seal & signatures */}
                        <div className="mt-12 flex items-end justify-between gap-6 max-w-3xl mx-auto">
                            <div className="text-left flex-1">
                                <div className="font-display italic text-xl">{cert.instructor}</div>
                                <div className="border-t border-[#1a1a1a]/30 mt-1 pt-1.5 text-[10px] uppercase tracking-widest font-semibold">Lead Instructor</div>
                            </div>

                            <div className="relative shrink-0">
                                <div className="w-24 h-24 rounded-full border-4 border-[#7C5CFF] flex items-center justify-center bg-white shadow-xl">
                                    <Award className="w-10 h-10" style={{ color: "#7C5CFF" }} />
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center -rotate-12">
                                    <span className="font-display font-black text-[10px] text-[#7C5CFF] tracking-widest opacity-60">VERIFIED</span>
                                </div>
                            </div>

                            <div className="text-right flex-1">
                                <div className="font-display italic text-xl">Dr. Mira Halsey</div>
                                <div className="border-t border-[#1a1a1a]/30 mt-1 pt-1.5 text-[10px] uppercase tracking-widest font-semibold">Director of Education</div>
                            </div>
                        </div>

                        <div className="mt-8 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#1a1a1a]/60 font-semibold max-w-3xl mx-auto">
                            <span>Issued {new Date(cert.issuedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                            <span className="font-mono">Verify: bluemountain.io/c/{cert.code}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
